import { Stepper } from "../../design-v2";

/*
 * The one Settings stepper composition: title, description, and a single
 * `[−] value [+]` row built from the canonical `Stepper`.
 *
 * Sessions per week and Duration per session are the same control family and
 * share this component, so there is exactly one implementation of the layout.
 * The preset/shortcut chip row that used to sit beneath the control was removed
 * — the stepper alone is the way a value is chosen. Nothing about the allowed
 * values, the bounds, the step semantics or the change callback changed: the
 * increment/decrement targets are still derived from `allowedValues` when they
 * are supplied, and from `min`/`max`/`step` otherwise.
 */
export default function InlineStepper({
  label,
  description,
  value,
  min,
  max,
  step = 1,
  allowedValues = [],
  onChange,
  suffix = "",
  disabled = false,
  valueText,
  formatValue,
}) {
  const numericValue = value == null || value === "" ? null : Number(value);
  const orderedValues = Array.isArray(allowedValues)
    ? allowedValues.map(Number).filter(Number.isFinite)
    : [];
  const usesOrderedValues = orderedValues.length > 0;
  const currentIndex = usesOrderedValues
    ? orderedValues.indexOf(numericValue)
    : -1;
  const previousValue = usesOrderedValues
    ? currentIndex > 0
      ? orderedValues[currentIndex - 1]
      : currentIndex === -1 && numericValue != null
        ? orderedValues.filter((candidate) => candidate < numericValue).at(-1) ?? null
        : null
    : numericValue == null
      ? min
      : clamp(numericValue - step);
  const nextValue = usesOrderedValues
    ? currentIndex >= 0 && currentIndex < orderedValues.length - 1
      ? orderedValues[currentIndex + 1]
      : currentIndex === -1 && numericValue != null
        ? orderedValues.find((candidate) => candidate > numericValue) ?? null
        : null
    : numericValue == null
      ? min
      : clamp(numericValue + step);

  function clamp(nextValue) {
    return Math.max(min, Math.min(max, nextValue));
  }

  return (
    <div className="lz-settings-stepper-block">
      <div>
        <p className="lz-settings-primary-text" aria-hidden="true">
          {label}
        </p>
        {description ? (
          <p className="lz-settings-section__description">{description}</p>
        ) : null}
      </div>

      <Stepper
        label={label}
        value={numericValue ?? "--"}
        valueText={
          valueText ?? (
            numericValue == null
              ? "--"
              : typeof formatValue === "function"
                ? formatValue(numericValue)
                : String(numericValue)
          )
        }
        unit={numericValue != null && suffix ? suffix : undefined}
        decrementLabel={`Decrease ${label}`}
        incrementLabel={`Increase ${label}`}
        canDecrement={!disabled && previousValue != null && previousValue !== numericValue}
        canIncrement={!disabled && nextValue != null && nextValue !== numericValue}
        disabled={disabled}
        onDecrement={() => previousValue != null && onChange(String(previousValue))}
        onIncrement={() => nextValue != null && onChange(String(nextValue))}
      />
    </div>
  );
}
